/**
 * VERSION D — "THE DELTA"
 *
 * The question this version asks: **can the whole episode be ONE place, seen from a moving
 * camera, instead of a series of slides?**
 *
 * There are no cuts between unrelated pictures. The 45 seconds is a single continuous descent
 * over a braided river delta, and the delta IS the argument: one channel entering, dividing
 * again and again into finer and finer waterways. That is a lineage drawn by water — a MAP,
 * which is the one thing Yash liked in v14, but spatial instead of a flow chart.
 *
 * The line drawn over it is a KOLAM figure (research 04 §Topic 3): a single unbroken continuous
 * curve, which is explicitly a symbol of unending continuity and is generated in one stroke.
 * ⛔ Because it never lifts, it can never read as an org chart. `pathLength="1"` + dashoffset
 * means the drawing of the line IS the animation of descent.
 *
 * The aerials are washed through an old cloth plate on `multiply`, so the frame reads as a chart
 * on fabric rather than a satellite photo — colour that lives in the ground, not a flat field.
 * Type: Biryani 900 (headline) / Khand 600 (labels).
 */
import React from "react";
import { AbsoluteFill, Audio, Img, staticFile, Easing } from "remotion";
import { loadFont as Biryani } from "@remotion/google-fonts/Biryani";
import { loadFont as Khand } from "@remotion/google-fonts/Khand";
import {
  Depth3D,
  Fog,
  Plate,
  Punch,
  Label,
  Grain,
  Vignette,
  useKeys,
  useSpringAt,
  devaBlock,
} from "./kit45";

const head = Biryani("normal", { weights: ["900", "700"], subsets: ["devanagari", "latin"] }).fontFamily;
const lab = Khand("normal", { weights: ["600"], subsets: ["devanagari", "latin"] }).fontFamily;

const sf = (p: string) => staticFile(p);
const ACCENT = "#E4633F";
/* ⛔ The raw aerials are flat mid-tone green-grey — white type and a white trace both die on
   them. Crushing the blacks and pulling saturation is what turns a photograph into a CHART. */
const GRADE = "contrast(1.34) saturate(0.52) brightness(0.74)";
const P = 3000;

/** the cloth the whole map is printed on — multiply, so it tints rather than covers */
const Cloth: React.FC<{ opacity?: number }> = ({ opacity = 0.52 }) => (
  <AbsoluteFill style={{ pointerEvents: "none", mixBlendMode: "multiply", opacity }}>
    <Img src={sf("sanatan45/D/D5_cloth_pano.png")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
  </AbsoluteFill>
);

/** a warm chart wash so the aerial stops being a photograph and becomes a printed map */
const ChartWash: React.FC<{ strength?: number }> = ({ strength = 0.42 }) => (
  <AbsoluteFill
    style={{
      pointerEvents: "none",
      background: `linear-gradient(196deg, rgba(232,214,176,${strength}) 0%, rgba(196,172,128,${strength * 0.6}) 46%, rgba(60,52,38,${strength * 0.9}) 100%)`,
      mixBlendMode: "soft-light",
    }}
  />
);

/**
 * Trace — one unbroken stroke drawn between two times. pathLength=1 makes dashoffset
 * resolution-independent, so the same path works at 720p and 4K.
 */
const Trace: React.FC<{
  d: string;
  from: number;
  to: number;
  color?: string;
  width?: number;
  opacity?: number;
  dash?: boolean;
}> = ({ d, from, to, color = ACCENT, width = 3, opacity = 1, dash }) => {
  const p = useKeys([[from, 0], [to, 1]], Easing.inOut(Easing.cubic));
  return (
    <svg data-audit="trace" viewBox="0 0 1280 720" preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={opacity}
        pathLength={1}
        strokeDasharray={dash ? "0.012 0.014" : 1}
        strokeDashoffset={dash ? 0 : 1 - p}
        style={dash ? { clipPath: `inset(0 ${(100 - p * 100).toFixed(1)}% 0 0)` } : undefined}
      />
    </svg>
  );
};

/** a marker pinned to a point on the map, with its label */
const Pin: React.FC<{ at: number; x: number; y: number; text: string; size?: number; accent?: boolean }> = ({
  at,
  x,
  y,
  text,
  size = 30,
  accent,
}) => {
  const s = useSpringAt(at, { damping: 12, stiffness: 200, mass: 0.7 });
  return (
    <div data-audit="pin" style={{ position: "absolute", left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)" }}>
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: 12,
          background: accent ? ACCENT : "#F2E9D8",
          boxShadow: `0 0 0 ${(s * 7).toFixed(1)}px rgba(196,85,61,${(0.28 * (1 - s)).toFixed(2)})`,
          transform: `scale(${s.toFixed(2)})`,
          margin: "0 auto",
        }}
      />
      <div style={{ marginTop: 7, opacity: s, transform: `translateY(${((1 - s) * 8).toFixed(1)}px)` }}>
        <span
          style={{
            ...devaBlock,
            fontFamily: lab,
            fontWeight: 600,
            fontSize: size,
            color: accent ? "#F6E8D6" : "rgba(246,232,214,0.9)",
            textShadow: "0 2px 12px rgba(20,16,10,0.95)",
            whiteSpace: "pre",
          }}
        >
          {text}
        </span>
      </div>
    </div>
  );
};

/* The delta's own branching, hand-authored as one continuous stroke — this is the kolam:
   it enters top-left, divides, and never lifts. */
const MAIN_CHANNEL =
  "M 90 96 C 240 150, 320 214, 402 292 C 470 356, 520 402, 604 438 C 700 480, 792 500, 906 528 C 1010 554, 1096 588, 1192 640";
const BRANCH_1 = "M 402 292 C 452 330, 486 402, 500 500 C 512 582, 496 636, 470 700";
const BRANCH_2 = "M 604 438 C 664 396, 736 366, 826 356 C 900 348, 962 352, 1030 366";
const BRANCH_3 = "M 906 528 C 936 462, 986 420, 1064 396";

export const VerD_Delta: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#0E120E" }}>
    <Audio src={sf("sanatan/audio/gotra45.mp3")} />

    {/* ── P1 0-7 · HIGHEST ALTITUDE. six places light up ─────────────────── */}
    <Plate from={0} to={7.0} lead={0}>
      <Depth3D
        P={P}
        layers={[
          { src: sf("sanatan45/D/D1_delta_pano.png"), z: 0, grade: GRADE },
        ]}
        cam={[
          [0, 60, -20, 0],
          [7.0, -30, 10, 88],
        ]}
      />
      <ChartWash />
      <Cloth />
      <Vignette strength={0.6} color="24,20,12" />
      <Trace d={MAIN_CHANNEL} from={0.3} to={6.4} color="rgba(246,232,214,0.85)" width={2.5} />
      {([[0.30, "जन्म", 12, 20], [1.58, "मुंडन", 28, 36], [2.40, "शादी", 41, 52], [3.28, "गृह प्रवेश", 56, 62], [4.26, "हवन", 71, 72], [5.94, "अंतिम संस्कार", 88, 86]] as [number, string, number, number][]).map(
        ([t, w, x, y]) => (
          <Pin key={w} at={t} x={x} y={y} text={w} size={28} />
        ),
      )}
      <Label at={0.8} text="एक ही सवाल, हर मोड़ पर" size={22} color="rgba(246,232,214,0.6)"
        style={{ position: "absolute", left: 74, bottom: 66, fontFamily: lab }} />
    </Plate>

    {/* ── P2 7-12.2 · DESCEND. the question over the land ─────────────────── */}
    <Plate from={7.0} to={12.2}>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D2_delta_mid.png"), z: 0, grade: GRADE }]}
        cam={[
          [7.0, 20, -14, 0],
          [12.2, -12, 8, 122],
        ]}
      >
        <Fog z={700} opacity={0.2} color="196,180,140" P={P} />
      </Depth3D>
      <ChartWash strength={0.34} />
      <Cloth opacity={0.42} />
      <Vignette strength={0.66} color="24,20,12" />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Punch at={10.69} text="आपका गोत्र क्या है" size={92} color="#F6EADA" weight={900}
            style={{ fontFamily: head, textShadow: "0 8px 44px rgba(14,12,8,0.95)" }} />
        </div>
      </AbsoluteFill>
      <Label at={7.5} text="पंडित जी सबसे पहले यही पूछते हैं" size={22} color="rgba(246,232,214,0.62)"
        style={{ position: "absolute", left: 74, top: 66, fontFamily: lab }} />
    </Plate>

    {/* ── P3 12.2-15.9 · ONE POINT ON THE MAP ────────────────────────────── */}
    <Plate from={12.2} to={15.9}>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D3_riverbank.png"), z: 0, grade: GRADE }]}
        cam={[
          [12.2, -20, 6, 0],
          [15.9, 10, -6, 96],
        ]}
      />
      <ChartWash strength={0.28} />
      <Cloth opacity={0.36} />
      <Vignette strength={0.62} color="18,20,22" />
      <Trace d="M 1180 92 C 980 150, 820 250, 690 372" from={14.5} to={15.5} color={ACCENT} width={3} dash />
      <Pin at={14.79} x={54} y={52} text="घर" size={34} accent />
      <Label at={12.9} text="हम में से ज़्यादातर" size={22} color="rgba(232,240,244,0.6)"
        style={{ position: "absolute", left: 74, top: 66, fontFamily: lab }} />
    </Plate>

    {/* ── P4 15.9-19 · THE RESET, still on the map ───────────────────────── */}
    <Plate from={15.9} to={19.0}>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D1_delta_pano.png"), z: 0, grade: GRADE }]}
        cam={[
          [15.9, -10, 0, 40],
          [19.0, 6, 0, 0],
        ]}
      />
      <ChartWash strength={0.5} />
      <Cloth opacity={0.6} />
      <Vignette strength={0.7} color="24,20,12" />
      <div style={{ position: "absolute", left: 78, top: "34%" }}>
        <Punch at={15.95} text="सीधे काम की बात" size={76} color="#F6EADA" weight={900} style={{ fontFamily: head }} />
        <div style={{ height: 12 }} />
        <Punch at={17.67} text="गोत्र होता क्या है" size={44} color={ACCENT} weight={700} style={{ fontFamily: head }} />
      </div>
    </Plate>

    {/* ── P5 19-22.7 · THREE CHANNELS NAMED ──────────────────────────────── */}
    <Plate from={19.0} to={22.7}>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D1_delta_pano.png"), z: 0, grade: GRADE }]}
        cam={[
          [19.0, 6, 0, 0],
          [22.7, -18, 6, 74],
        ]}
      />
      <ChartWash strength={0.44} />
      <Cloth opacity={0.5} />
      <Vignette strength={0.62} color="24,20,12" />
      <Trace d={MAIN_CHANNEL} from={19.0} to={19.9} color="rgba(246,232,214,0.9)" width={3} />
      <Trace d={BRANCH_1} from={21.25} to={22.0} color={ACCENT} width={2.6} />
      <Trace d={BRANCH_2} from={22.02} to={22.7} color={ACCENT} width={2.6} />
      <Pin at={19.2} x={31} y={41} text="गोत्र" size={30} />
      <Pin at={21.25} x={38} y={78} text="प्रवर" size={30} accent />
      <Pin at={22.02} x={78} y={49} text="सपिंड" size={30} accent />
    </Plate>

    {/* ── P6 22.7-29.9 · FOUR SUB-CHANNELS ───────────────────────────────── */}
    <Plate from={22.7} to={29.9}>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D2_delta_mid.png"), z: 0, grade: GRADE }]}
        cam={[
          [22.7, -14, -8, 0],
          [29.9, 16, 8, 108],
        ]}
      />
      <ChartWash strength={0.36} />
      <Cloth opacity={0.44} />
      <Vignette strength={0.62} color="24,20,12" />
      <Trace d={BRANCH_3} from={22.9} to={24.4} color="rgba(246,232,214,0.8)" width={2.4} />
      {([[22.75, "ये क्या हैं", 16, 30], [23.66, "महत्व", 38, 58], [25.44, "शादी में क्या", 63, 34], [27.72, "विज्ञान", 85, 64]] as [number, string, number, number][]).map(
        ([t, w, x, y], i) => (
          <Pin key={w} at={t} x={x} y={y} text={w} size={28} accent={i === 3} />
        ),
      )}
    </Plate>

    {/* ── P7 29.9-37.1 · UPSTREAM TO THE SOURCE, THEN THE FORK ───────────── */}
    <Plate from={29.9} to={37.1}>
      <SevenD />
    </Plate>

    {/* ── P8 37.1-45.6 · THE KOLAM — one unbroken line ────────────────────── */}
    <Plate from={37.1} to={45.6} tail={0}>
      <EightD />
    </Plate>

    <Grain opacity={0.16} freq={0.8} />
  </AbsoluteFill>
);

const SevenD: React.FC = () => {
  const fork = useKeys([[35.32, 0], [36.2, 1]], Easing.inOut(Easing.cubic));
  return (
    <>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D1_delta_pano.png"), z: 0, grade: GRADE }]}
        cam={[
          [29.9, 40, 14, 96],
          [33.2, 74, 20, 20],
          [37.1, 66, 16, 6],
        ]}
      />
      <ChartWash strength={0.46} />
      <Cloth opacity={0.5} />
      <Vignette strength={0.68} color="24,20,12" />
      {/* the whole braid, traced backwards to where it starts */}
      <Trace d={MAIN_CHANNEL} from={29.9} to={32.4} color="rgba(246,232,214,0.9)" width={3} />
      <div style={{ position: "absolute", left: 78, top: "22%" }}>
        <Punch at={32.36} text="जड़" size={124} color="#F6EADA" weight={900} out={34.8}
          style={{ fontFamily: head, textShadow: "0 8px 40px rgba(14,12,8,0.95)" }} />
      </div>
      <Label at={30.2} text="इस पूरे विषय की" size={24} color="rgba(246,232,214,0.62)"
        style={{ position: "absolute", left: 82, top: "17%", fontFamily: lab }} />
      {/* one source, two systems: the channel forks */}
      <Trace d="M 250 200 C 380 250, 470 330, 540 440" from={35.32} to={36.3} color="#F6EADA" width={4} />
      <Trace d="M 250 200 C 400 210, 520 240, 640 250" from={35.7} to={36.7} color="rgba(246,234,218,0.45)" width={4} />
      <div style={{ position: "absolute", left: "40%", top: "68%", opacity: fork }}>
        <span style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 40, color: "#F6EADA" }}>पहली</span>
      </div>
      <div style={{ position: "absolute", left: "54%", top: "31%", opacity: fork }}>
        <span style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 40, color: "rgba(246,234,218,0.5)" }}>दूसरी</span>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 58, textAlign: "center", opacity: fork }}>
        <span style={{ ...devaBlock, fontFamily: lab, fontWeight: 600, fontSize: 26, color: "rgba(246,232,214,0.82)" }}>
          दोनों असली हैं
        </span>
      </div>
    </>
  );
};

const EightD: React.FC = () => {
  const headIn = useSpringAt(39.79, { damping: 16, stiffness: 155 });
  return (
    <>
      <Depth3D
        P={P}
        layers={[{ src: sf("sanatan45/D/D4_kolam.png"), z: 0, grade: GRADE }]}
        cam={[
          [37.1, -12, 8, 0],
          [42.0, 0, 0, 62],
          [45.6, 8, -6, 146],
        ]}
      />
      <ChartWash strength={0.3} />
      <Cloth opacity={0.34} />
      <Vignette strength={0.66} color="18,14,10" />
      {/* the heading arrives and STAYS for its section */}
      <div style={{ position: "absolute", left: 78, top: 64, opacity: headIn, transform: `translateY(${((1 - headIn) * 22).toFixed(1)}px)` }}>
        <span style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 58, color: "#F6EADA" }}>आर्ष गोत्र</span>
        <div style={{ height: 3, background: ACCENT, transform: `scaleX(${headIn.toFixed(3)})`, transformOrigin: "0% 50%", marginTop: 3 }} />
      </div>
      <div style={{ position: "absolute", left: 82, top: 168 }}>
        <Punch at={42.03} text="ऋषि से जुड़ा हुआ" size={32} color="rgba(246,232,214,0.85)" weight={600} style={{ fontFamily: lab }} />
      </div>
      {/* the unbroken line climbing back — it does not finish, and that is the open loop */}
      <Trace
        d="M 300 660 C 420 600, 470 520, 470 430 C 470 350, 540 300, 640 292 C 750 284, 820 236, 840 160"
        from={43.6}
        to={45.6}
        color={ACCENT}
        width={4}
      />
      <Pin at={43.81} x={37} y={60} text="पिता" size={30} accent />
      <Pin at={45.33} x={50} y={41} text="उनके पिता" size={30} accent />
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
